import { defineStore } from "pinia";

import { requestS } from "@/helpers";
import { router } from "@/router";
import { useAlertStore } from "@/stores";

const baseUrl = `${process.env.VUE_APP_API_URL}`;

export const useDraftProposalStore = defineStore({
  id: "draftProposal",
  state: () => ({
    draftProposal: null,
  }),
  actions: {
    async all(perPage = 20, currentPage = 1) {
      try {
        return await requestS
          .get(
            `${baseUrl}/propostas-rascunho?per_page=${perPage}&page=${currentPage}`
          )
          .then((response) => {
            return response;
          })
          .catch((error) => {
            return [];
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },

    async find(draftProposalId) {
      try {
        return await requestS
          .get(`${baseUrl}/propostas-rascunho/${draftProposalId}`)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            return null;
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },

    async findByEdict(edictId = "") {
      try {
        return await requestS
          .get(`${baseUrl}/propostas-rascunho/edital/${edictId}`)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            return null;
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },

    async store(data) {
      try {
        let answers = [];
        let members = [];
        if (data.edict_id) {
          data.team_members?.map((member) =>
            members.push({ nome: member.name, experiencia: member.formation })
          );
        }

        if (data.edict === "incube") {
          answers = incubeData(data);
        } else if (data.edict === "inpulse") {
          answers = inpulseData(data);
        } else if (data.edict === "inspire") {
          answers = inspireData(data);
        } else if (data.edict === "continuos-flow") {
          answers = continuosFlowData(data);
          members = "";
        } else {
          return false;
        }

        const request = {};
        data.project_name ? (request.nome_proposta = data.project_name) : "";
        data.edict_id ? (request.edital_id = data.edict_id) : "";
        answers ? (request.perguntas_e_respostas = answers) : "";
        members.length > 0 ? (request.integrantes = members) : "";

        // Handle specialty_areas - can be array of IDs, array of objects, or single value
        if (data.specialty_areas) {
          const specialties = Array.isArray(data.specialty_areas) ? data.specialty_areas : [data.specialty_areas];
          const specialtyIds = specialties.map(s => typeof s === 'object' ? s?.id : s).filter(Boolean);
          if (specialtyIds.length > 0) {
            request.especialidades = specialtyIds;
          }
        }

        return await requestS
          .post(`${baseUrl}/propostas-rascunho`, request)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            const alertStore = useAlertStore();
            alertStore.error(error);
          });
      } catch (error) {
        console.log(error);
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },

    async storeOrUpdate(data, silent = false) {
      try {
        let answers = [];
        let members = [];
        if (data.edict_id) {
          data.team_members?.map((member) =>
            members.push({ nome: member.name, experiencia: member.formation })
          );
        }

        if (data.edict === "incube") {
          answers = incubeData(data);
        } else if (data.edict === "inpulse") {
          answers = inpulseData(data);
        } else if (data.edict === "inspire") {
          answers = inspireData(data);
        } else if (data.edict === "continuos-flow") {
          answers = continuosFlowData(data);
          members = "";
        } else {
          return false;
        }

        // Guard: on silent auto-saves, skip if answers has no meaningful data.
        // This prevents accidentally overwriting complete backend data with an
        // empty or near-empty payload (e.g. after field de-registration on step change).
        if (silent && !hasMeaningfulAnswers(answers)) {
          return;
        }

        const request = {};
        data.project_name ? (request.nome_proposta = data.project_name) : "";
        data.edict_id ? (request.edital_id = data.edict_id) : "";
        answers ? (request.perguntas_e_respostas = answers) : "";
        members.length > 0 ? (request.integrantes = members) : "";

        // Handle specialty_areas - can be array of IDs, array of objects, or single value
        if (data.specialty_areas) {
          const specialties = Array.isArray(data.specialty_areas) ? data.specialty_areas : [data.specialty_areas];
          const specialtyIds = specialties.map(s => typeof s === 'object' ? s?.id : s).filter(Boolean);
          if (specialtyIds.length > 0) {
            request.especialidades = specialtyIds;
          }
        }

        return await requestS
          .post(`${baseUrl}/propostas-rascunho/store-or-update`, request)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            if (!silent) {
              const alertStore = useAlertStore();
              alertStore.error(error);
            }
          });
      } catch (error) {
        if (!silent) {
          console.log(error);
          const alertStore = useAlertStore();
          alertStore.error(error);
        }
      }
    },

    async storeFile(proposal_id, data) {
      try {
        const formData = new FormData();
        formData.append("proposta_id", proposal_id);
        data.business_model
          ? formData.append("modelo_de_negocio", data.business_model)
          : "";
        data.pitch ? formData.append("pitch", data.pitch) : "";

        return await requestS
          .upload(
            `${baseUrl}/propostas-rascunho/adicionar-documentos`,
            formData
          )
          .then((response) => {
            return response;
          })
          .catch((error) => {
            const alertStore = useAlertStore();
            alertStore.error(error);
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
        console.log(error);
      }
    },

    async update(draftProposalId, data) {
      try {
        return await requestS
          .put(`${baseUrl}/propostas-rascunho/${draftProposalId}`, {
            nome: data.name,
            tipo: data.type,
            id_instituto: data.institute_id,
            id_usuario: data.user_id,
          })
          .then((response) => {
            return response;
          })
          .catch((error) => {
            const alertStore = useAlertStore();
            alertStore.error(error);
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },

    async delete(draftProposalId) {
      try {
        return await requestS
          .delete(`${baseUrl}/propostas-rascunho/${draftProposalId}`)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            const alertStore = useAlertStore();
            alertStore.error(error);
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },
    async allWithoutPagination() {
      try {
        return await requestS
          .get(`${baseUrl}/propostas-rascunho/todas`)
          .then((response) => {
            return response;
          })
          .catch((error) => {
            return [];
          });
      } catch (error) {
        const alertStore = useAlertStore();
        alertStore.error(error);
      }
    },
  },
});

function continuosFlowData(data) {
  return {
    conditions: data.conditions,
    formation_experience: data.formation_experience,
    heard_about: data.heard_about,
    project_secrecy: data.project_secrecy,
    fmusp_connection: data.fmusp_connection,
    team: data.team,
    team_composition: data.team_composition,
    team_members: data.team_members,
    team_members_formation: data.team_members_formation,
    project_model: data.project_model,
    partners: data.partners,
    has_financy: data.has_financy,
    development_stage: data.development_stage,
    cnpj: data.cnpj,
    project_inpi: data.project_inpi,
    project_publication: data.project_publication,
    intellectual_support: data.intellectual_support,
    presented_orally: data.presented_orally,
    has_search: data.has_search,
    approved_project: data.approved_project,
    clinic_support: data.clinic_support,
    has_regulation: data.has_regulation,
    regulation_support: data.regulation_support,
    sensitive_data: data.sensitive_data,
    sensitive_data_support: data.sensitive_data_support,
    help: data.help,
    secondary_specialty_areas: data.secondary_specialty_areas,
    project_feature: data.project_feature,
    project_description: data.project_description,
    project_problems: data.project_problems,
    project_solutions: data.project_solutions,
    people_affected: data.people_affected,
    project_offer: data.project_offer,
    project_scale: data.project_scale,
  };
}

function incubeData(data) {
  return {
    conditions: data.conditions,
    formation_experience: data.formation_experience,
    team_composition: data.team_composition,
    team_members: data.team_members,
    team_members_formation: data.team_members_formation,
    team_motivation: data.team_motivation,
    support_types: data.support_types,
    heard_about: data.heard_about,
    project_feature: data.project_feature,
    project_description: data.project_description,
    project_problems: data.project_problems,
    people_affected: data.people_affected,
    project_solutions: data.project_solutions,
    project_offer: data.project_offer,
    project_expectations: data.project_expectations,
    project_scale: data.project_scale,
    development_stage: data.development_stage,
    project_inpi: data.project_inpi,
    project_publication: data.project_publication,
    presented_orally: data.presented_orally,
    received_promotion: data.received_promotion,
    expected_outcome: data.expected_outcome,
    project_files: JSON.stringify(data.project_files),
  };
}

function inpulseData(data) {
  return {
    conditions: data.conditions,
    formation_experience: data.formation_experience,
    fmusp_connection: data.fmusp_connection,
    project_secrecy: data.project_secrecy,
    team_composition: data.team_composition,
    team_members: data.team_members,
    team_members_formation: data.team_members_formation,
    team_motivation: data.team_motivation,
    support_types: data.support_types,
    heard_about: data.heard_about,
    development_stage: data.development_stage,
    project_inpi: data.project_inpi,
    intellectual_support: data.intellectual_support,
    has_investiment: data.has_investiment,
    has_search: data.has_search,
    clinic_support: data.clinic_support,
    has_regulation: data.has_regulation,
    regulation_support: data.regulation_support,
    sensitive_data: data.sensitive_data,
    sensitive_data_support: data.sensitive_data_support,
    received_promotion: data.received_promotion,
    commercial_exploitation: data.commercial_exploitation,
    execution: data.execution,
    project_feature: data.project_feature,
    project_description: data.project_description,
    key_words: data.key_words,
    project_problems: data.project_problems,
    people_affected: data.people_affected,
    project_solutions: data.project_solutions,
    project_offer: data.project_offer,
    project_expectations: data.project_expectations,
    project_scale: data.project_scale,
    has_firm: data.has_firm,
    firm_support: data.firm_support,
    expected_outcome: data.expected_outcome,
    fmusp_connection_type: data.fmusp_connection_type,
    fmusp_institute: data.fmusp_institute,
    fmusp_contact: data.fmusp_contact,
    fmusp_institute_work: data.fmusp_institute_work,
    fmusp_institute_from_not_anymore: data.fmusp_institute_from_not_anymore,
    fmusp_institute_from_no: data.fmusp_institute_from_no,
  };
}

function inspireData(data) {
  return {
    conditions: data.conditions,
    formation_experience: data.formation_experience,
    fmusp_connection: data.fmusp_connection,
    project_secrecy: data.project_secrecy,
    team_composition: data.team_composition,
    team_members: data.team_members,
    team_members_formation: data.team_members_formation,
    team_motivation: data.team_motivation,
    support_types: data.support_types,
    heard_about: data.heard_about,
    project_inpi: data.project_inpi,
    sensitive_data: data.sensitive_data,
    sensitive_data_support: data.sensitive_data_support,
    received_promotion: data.received_promotion,
    project_feature: data.project_feature,
    project_description: data.project_description,
    project_problems: data.project_problems,
    people_affected: data.people_affected,
    project_solutions: data.project_solutions,
    project_offer: data.project_offer,
    project_validation: data.project_validation,
    project_scale: data.project_scale,
    project_files: JSON.stringify(data.project_files),
  };
}

function reviewData(data) {
  return {
    conditions: data.conditions,
    has_conflict: data.has_conflict,
    innovation: data.innovation,
    viability: data.viability,
    solution_potential: data.solution_potential,
    scale_potential: data.scale_potential,
    compatibility: data.compatibility,
    justify: data.justify,
    has_conflict_answer: data.has_conflict_answer,
    challenges: data.challenges,
    recomendations: data.recomendations,
  };
}

function isEmptyObject(obj) {
  for (const key in obj) {
    if (Object.hasOwn(obj, key)) {
      return false;
    }
  }
  return true;
}

// Returns true if `answers` contains at least one field with actual data
// (non-null, non-empty string, non-empty array). Used to guard against
// silent auto-saves that would overwrite complete backend data with an
// empty/partial payload caused by vee-validate field de-registration on
// step navigation.
function hasMeaningfulAnswers(answers) {
  if (!answers || Array.isArray(answers)) return false;
  for (const val of Object.values(answers)) {
    if (val === undefined || val === null || val === "") continue;
    if (Array.isArray(val)) {
      if (val.length > 0) return true;
    } else {
      return true;
    }
  }
  return false;
}
